import { type GrantablePair, permissionManifest, scopeForGrant } from "@msuite/citadel";
import type { RolePermissionFormSchema } from "@msuite/utils/schemas";
import type { RoleGrant } from "@msuite/utils/types";
import { grantKey } from "@/components/citadel";

// Re-exported so the editor's own files keep one import site; the definition is shared with
// the user grid in @/components/citadel.
export { grantKey };

/** The subjects the manifest declares; a stored grant naming anything else is not renderable. */
const MANIFEST_SUBJECTS = new Set(permissionManifest.map(({ subject }) => subject));

/** The stored row's pair, in the shape the scope map addresses a cell by. */
function grantOf(permission: RoleGrant) {
	return { subject: permission.subject, action: permission.action } as GrantablePair;
}

/**
 * The editor's starting state: every pair the manifest declares, `false` unless the role holds
 * it, in which case the scope its stored conditions encode. A stored grant whose template the
 * scope map does not recognise stays `false` here and is rendered read-only instead — see
 * `customGrantKeys`.
 */
export function toGrantDefaults(permissions: RoleGrant[]): RolePermissionFormSchema["grants"] {
	const grants: RolePermissionFormSchema["grants"] = Object.fromEntries(
		permissionManifest.flatMap(({ subject, actions }) =>
			actions.map((action) => [grantKey(action, subject), false as const]),
		),
	);

	for (const permission of permissions) {
		const key = grantKey(permission.action, permission.subject);
		if (!(key in grants)) continue;

		const scope = scopeForGrant(grantOf(permission), permission.conditions);
		if (scope) grants[key] = scope;
	}

	return grants;
}

/**
 * The keys whose stored reach the cell cannot represent: a conditions template the scope map did
 * not produce, or one whose reach the pair no longer offers. The editor renders those cells
 * read-only rather than showing the nearest scope, because showing a scope would misstate what
 * the grant actually reaches.
 */
export function customGrantKeys(permissions: RoleGrant[]): Set<string> {
	return new Set(
		permissions
			.filter((permission) => MANIFEST_SUBJECTS.has(permission.subject))
			.filter((permission) => scopeForGrant(grantOf(permission), permission.conditions) === null)
			.map((permission) => grantKey(permission.action, permission.subject)),
	);
}

/**
 * The payload the editor sends: its state without the cells it rendered read-only.
 *
 * Leaving those keys out does not preserve them — the write replaces a role's rows wholesale,
 * so a custom grant is dropped the first time anybody saves. That is acceptable because the
 * editor is the only writer of `rolePermissions.conditions` and every scope it can name comes
 * from the scope map, so an unrecognised template cannot arise through any current path. If one
 * ever does, the cell says so and the payload stays honest about what the editor understands,
 * instead of rewriting the grant to the nearest scope behind the user's back.
 */
export function withoutCustomKeys(
	grants: RolePermissionFormSchema["grants"],
	customKeys: Set<string>,
): RolePermissionFormSchema["grants"] {
	return Object.fromEntries(Object.entries(grants).filter(([key]) => !customKeys.has(key)));
}
